import throttle from 'lodash.throttle';
import anime from 'animejs/lib/anime.es.js';

export default class Menu {
	constructor(header) {
		this.header = header;
		this.menu = header.querySelector('.c-header-menu');
		this.button = header.querySelector('.c-header__menu-button');
		this.scrollY = window.scrollY;
		this.breakpoint = window.matchMedia('(max-width:575px)');
		this.menuOpen = false;
		this.isAnimating = false;
		this.duration = 200;
		this.fastDuration = 200;

		const menuStyle = getComputedStyle(this.menu);

		this.dimensions = {
			desktop: {
				menu: menuStyle.getPropertyValue('--menu-height-desktop'),
				submenu1: menuStyle.getPropertyValue('--submenu-1-height-desktop'),
				submenu2: menuStyle.getPropertyValue('--submenu-2-height-desktop'),
			},
			mobile: {
				menu: menuStyle.getPropertyValue('--menu-height-mobile'),
				submenu1: menuStyle.getPropertyValue('--submenu-1-height-mobile'),
				submenu2: menuStyle.getPropertyValue('--submenu-2-height-mobile'),
			},
		};

		this.minScrollDistance = {
			desktop: 100,
			mobile: 30,
		};
	}

	init() {
		this.button.addEventListener('click', e => {
			if (this.isAnimating) {
				return;
			}

			this.toggleMenu();
		});

		const subMenuButtons = this.menu.querySelectorAll('.menu-item-has-children > a');

		subMenuButtons.forEach(button => {
			button.addEventListener('click', e => {
				e.preventDefault();

				if (this.isAnimating) {
					return;
				}

				this.toggleSubMenu(e.target);
			});
		});

		document.addEventListener(
			'scroll',
			throttle(e => this.checkScroll(e), 100),
		);

		// Als je op de homepage bent, open het menu als dat nog niet eerder gedaan is
		if (document.body.classList.contains('home')) {
			const visited = sessionStorage.getItem('visited');

			if (!visited) {
				this.openMenu();
				sessionStorage.setItem('visited', true);
			}
		}
	}

	getDimension(type) {
		const width = this.breakpoint.matches === true ? 'mobile' : 'desktop';

		return this.dimensions[width][type].trim().replace('px', '');
	}

	getMinScrollDistance() {
		const query = window.matchMedia('(max-width:1024px) and (orientation: landscape)');
		const platform = query.matches === true ? 'mobile' : 'desktop';

		return this.minScrollDistance[platform];
	}

	checkScroll(e = null) {
		// if (this.breakpoint.matches === true) {
		// 	return;
		// }

		const classList = this.header.classList;

		if (this.scrollY < window.scrollY && window.scrollY > this.getMinScrollDistance()) {
			// ↓
			if (!classList.contains('is-up') && !this.isAnimating) {
				if (this.menuOpen) {
					this.closeMenu().then(() => {
						classList.remove('is-down');
						classList.add('is-up');
					});
				} else {
					classList.remove('is-down');
					classList.add('is-up');
				}
			}
		} else {
			// ↑
			classList.add('is-down');
			classList.remove('is-up');
		}

		this.scrollY = window.scrollY;
	}

	toggleMenu() {
		this.menuOpen ? this.closeMenu() : this.openMenu();
	}

	openMenu() {
		this.isAnimating = true;

		const menuItems = this.menu.querySelectorAll('.c-header-menu__nav > .menu-item');
		const tl = anime.timeline({
			easing: 'linear',
			duration: 250,
		});

		anime.set(menuItems, {
			opacity: 0,
		});

		tl.add({
			targets: this.menu,
			height: [0, this.getDimension('menu')],
			easing: 'easeInSine',
			duration: 200,
		}).add({
			targets: menuItems,
			opacity: 1,
		});

		tl.finished.then(() => {
			this.menuOpen = true;
			this.isAnimating = false;
		});

		document.body.classList.add('has-menu-open');
		this.menu.classList.add('is-active');
		this.button.classList.add('is-active');

		const menuOpenEvent = new CustomEvent('menuopen');
		document.dispatchEvent(menuOpenEvent);
	}

	closeMenu() {
		if (this.isAnimating || !this.menuOpen) {
			return new Promise(resolve => {
				resolve();
			});
		}

		return new Promise(resolve => {
			this.isAnimating = true;

			// Sluit eerst openstaande menu's
			this.closeSubmenus().then(() => {
				const menuItems = this.menu.querySelectorAll('.c-header-menu__nav > .menu-item');
				const tl = anime.timeline({
					easing: 'linear',
					duration: 250,
				});

				anime.set(menuItems, {
					opacity: 1,
				});

				tl.add({
					targets: menuItems,
					opacity: 0,
				}).add({
					targets: this.menu,
					height: [this.getDimension('menu'), 0],
					easing: 'easeInSine',
					duration: 200,
				});

				tl.finished.then(() => {
					this.menu.classList.remove('is-active');
					document.body.classList.remove('has-menu-open');
					this.menuOpen = false;
					this.isAnimating = false;

					const menuClosedEvent = new CustomEvent('menuclosed');
					document.dispatchEvent(menuClosedEvent);

					resolve();
				});

				this.button.classList.remove('is-active');
			});
		});
	}

	toggleSubMenu(button) {
		const parent = button.parentNode;
		const subMenu = parent.querySelector('.sub-menu');
		const classList = subMenu.classList;
		const level = subMenu.dataset.level;

		if (classList.contains('is-active')) {
			// Sluit een sub-menu
			if (level == '1') {
				// Controleer of er een sub-menu van level 2 gesloten moet worden, zo niet resolve direct
				const activeSubmenuPromise = new Promise(resolve => {
					const activeSubmenu = parent.querySelector('.sub-menu.is-active[data-level="2"]');

					if (activeSubmenu) {
						this.closeSubmenuLevel2(activeSubmenu).then(() => {
							resolve();
						});
					} else {
						resolve();
					}
				});

				activeSubmenuPromise.then(() => {
					this.closeSubmenuLevel1(subMenu).then(() => {
						this.isAnimating = false;
					});
				});
			}

			if (level == '2') {
				this.closeSubmenuLevel2(subMenu).then(() => {
					this.isAnimating = false;
				});
			}
		} else {
			// Open een sub-menu

			// Aanname: op level 1 kunnen er geen andere submenu's open zijn
			if (level == '1') {
				this.openSubmenuLevel1(subMenu).then(() => {
					this.isAnimating = false;
				});
			}

			if (level == '2') {
				// Vind een actief submenu, als dat er is sluit dat en open daarna het nieuwe submenu
				// Als er geen actief submenu is, resolve direct de promise
				const activeSubmenuPromise = new Promise(resolve => {
					const activeSubmenu = parent.parentNode.querySelector('.sub-menu.is-active');

					if (activeSubmenu) {
						this.closeSubmenuLevel2(activeSubmenu).then(() => {
							resolve();
						});
					} else {
						resolve();
					}
				});

				activeSubmenuPromise.then(() => {
					this.openSubmenuLevel2(subMenu).then(() => {
						this.isAnimating = false;
					});
				});
			}
		}
	}

	closeSubmenus(fast = true) {
		const subMenu1 = this.menu.querySelector('.sub-menu.is-active[data-level="1"]');
		const subMenu2 = this.menu.querySelector('.sub-menu.is-active[data-level="2"]');

		if (fast) {
			return new Promise(resolve => {
				let promises = [];

				promises.push(
					new Promise(resolve => {
						if (subMenu2) {
							this.closeSubmenuLevel2(subMenu2).then(resolve);
						} else {
							resolve();
						}
					}),
				);

				promises.push(
					new Promise(resolve => {
						if (subMenu1) {
							this.closeSubmenuLevel1(subMenu1).then(resolve);
						} else {
							resolve();
						}
					}),
				);

				resolve();
			});
		} else {
			const subMenu2Promise = new Promise(resolve => {
				if (subMenu2) {
					this.closeSubmenuLevel2(subMenu2).then(resolve);
				} else {
					resolve();
				}
			});

			return subMenu2Promise.then(() => {
				return new Promise(resolve => {
					if (subMenu1) {
						this.closeSubmenuLevel1(subMenu1).then(resolve);
					} else {
						resolve();
					}
				});
			});
		}
	}

	closeSubmenuLevel1(menu, duration = 200) {
		return new Promise(resolve => {
			this.isAnimating = true;

			const menuItems = menu.querySelectorAll(':scope > .menu-item');
			const tl = anime.timeline({
				easing: 'linear',
				duration: duration,
			});

			anime.set(menuItems, {
				opacity: 1,
			});

			tl.add({
				targets: menuItems,
				opacity: 0,
			}).add({
				targets: menu,
				height: [this.getDimension('submenu1'), 0],
				easing: 'easeOutSine',
				// duration: duration,
			});

			tl.finished.then(() => {
				menu.classList.remove('is-active');
				resolve();
			});
		});
	}

	closeSubmenuLevel2(menu, duration = 200) {
		return new Promise(resolve => {
			this.isAnimating = true;

			const menuItems = menu.querySelector(':scope > ul');
			const tl = anime.timeline({
				easing: 'linear',
				duration: duration,
			});

			tl.add({
				targets: menuItems,
				opacity: 0,
			}).add({
				targets: menu,
				height: [this.getDimension('submenu2'), 0],
				easing: 'easeOutSine',
				// duration: duration,
			});

			tl.finished.then(() => {
				menu.classList.remove('is-active');
				resolve();
			});
		});
	}

	openSubmenuLevel1(menu) {
		return new Promise(resolve => {
			this.isAnimating = true;

			const menuItems = menu.querySelectorAll(':scope > .menu-item');
			const tl = anime.timeline({
				easing: 'linear',
				duration: 250,
			});

			anime.set(menuItems, {
				opacity: 0,
			});

			tl.add({
				targets: menu,
				height: [0, this.getDimension('submenu1')],
				easing: 'easeInSine',
				duration: 200,
			}).add({
				targets: menuItems,
				opacity: 1,
			});

			tl.finished.then(() => {
				menu.classList.add('is-active');
				resolve();
			});
		});
	}

	openSubmenuLevel2(menu) {
		return new Promise(resolve => {
			this.isAnimating = true;

			const menuItems = menu.querySelector(':scope > ul');

			const tl = anime.timeline({
				easing: 'linear',
				duration: 250,
			});

			anime.set(menuItems, {
				opacity: 0,
			});

			tl.add({
				targets: menu,
				height: [0, menuItems.offsetHeight],
				easing: 'easeInSine',
				duration: 200,
			}).add({
				targets: menuItems,
				opacity: 1,
			});

			tl.finished.then(() => {
				menu.classList.add('is-active');
				resolve();
			});
		});
	}
}
